import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { REFERENCE_PAGE_SIZE_MAX, type ReferenceGroupView } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { SearchField } from '../ui/SearchField.js';
import { SideDialog } from '../team/SideDialog.js';
import { ReferenceThumb } from './ReferenceCard.js';
import { errorText, useDebounced, useInvalidateReferences, useReferenceLibrary } from './hooks.js';

/**
 * PUT REFERENCES THAT ALREADY EXIST INTO A GROUP — the empty group's other way
 * in. Nothing is copied: a reference in two groups is one reference.
 */
export function AddExistingDialog({
  projectId,
  group,
  onClose,
}: {
  projectId: string;
  group: ReferenceGroupView;
  onClose: () => void;
}) {
  const invalidate = useInvalidateReferences(projectId);
  const [q, setQ] = useState('');
  const settled = useDebounced(q, 300);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const library = useReferenceLibrary(projectId, { sort: 'RECENT', ...(settled.trim() !== '' ? { q: settled.trim() } : {}) });
  const cards = (library.data?.pages ?? []).flatMap((p) => p.items).filter((c) => !c.groups.some((g) => g.id === group.id));

  const add = useMutation({
    mutationFn: () => api.addToReferenceGroup(projectId, group.id, chosen),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (e) => {
      setError(errorText(e, 'The references could not be added to the group.'));
    },
  });

  return (
    <SideDialog title={`Add to ${group.name}`} testId="ref-add-existing" size="lg" onClose={onClose}>
      <SearchField value={q} onChange={setQ} placeholder="Search the library" label="Search the library" testId="ref-add-existing-search" />
      {library.isPending ? <p className="ref-quiet">Loading…</p> : null}
      {library.data && cards.length === 0 ? (
        <p className="ref-quiet" data-testid="ref-add-existing-empty">
          {settled.trim() === '' ? 'Every reference is already in this group.' : 'Nothing matches that search.'}
        </p>
      ) : null}
      {cards.length > 0 ? (
        <ul className="ref-pick-list" aria-label="References">
          {cards.map((c) => {
            const on = chosen.includes(c.id);
            return (
              <li key={c.id}>
                <label className="ref-pick-row" data-testid={`ref-add-existing-${c.id}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!on && chosen.length >= REFERENCE_PAGE_SIZE_MAX}
                    onChange={() => {
                      setChosen((current) => (on ? current.filter((id) => id !== c.id) : [...current, c.id]));
                    }}
                  />
                  <ReferenceThumb card={c} size="row" />
                  <span className="ref-pick-title">{c.title}</span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
      {library.hasNextPage ? (
        <button
          type="button"
          className="ctl"
          data-testid="ref-add-existing-more"
          disabled={library.isFetchingNextPage}
          onClick={() => {
            void library.fetchNextPage();
          }}
        >
          Load more
        </button>
      ) : null}
      {error !== null ? (
        <p className="ref-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="ref-add-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={chosen.length === 0 || add.isPending}
          data-testid="ref-add-existing-submit"
          onClick={() => {
            add.mutate();
          }}
        >
          {chosen.length === 0 ? 'Add to group' : `Add ${String(chosen.length)} to group`}
        </button>
        <button type="button" className="ctl" data-testid="ref-add-existing-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </SideDialog>
  );
}
